import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { FormControlLabel, FormLabel, FormControl, RadioGroup, Radio } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import Calendar from '@/components/UI/Calendar/Calendar';
import { ContainerGrid, ContainerGridItem } from '@/components/UI/ContainerGrid/ContainerGrid';
import DisclaimerCheckbox from '@/components/UI/DisclaimerCheckbox/DisclaimerCheckbox';
import InputText from '@/components/UI/InputText/InputText';
import InputUser from '@/components/UI/InputUser/InputUser';
import LabelDisabledByPDRS from '@/components/UI/LabelDisabledByPDRS/LabelDisabledByPDRS';
import {
  additionalContactPerson,
  ADDRESS,
  DESCRIPTION,
  FIELD_PARAMS,
  fieldTypes,
  HELP_AREA,
  NAME,
  NUMBER,
  POSSIBLE_CAUSER_INPUT_IDS,
  stateTabs,
  statusCampaign,
} from '@/helpers/constants';
import { stringToDate, checkDate, removeSpacesFromString, buildNewSelectedUser, isFieldDisabledBYPRRD } from '@/helpers/utils';
import useWarningField from '@/hooks/useWarningField';
import { setDescriptionValidations } from '@/redux/Campaign/campaign.action';
import { getCampaignInfo, getCampaignDescription, getDescriptionValidations, getOriginalValues } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import { getParamConfiguration } from '@/redux/ParamsConfig/paramsConfig.reducer';
import { CampaignInfo, LangTextMandatory, Validations } from '@/types';
import { Description as DescriptionType } from '@/types/Description';
import { getMessage } from './Description.utils';
import styles from './Description.module.scss';

const getSupplierInputTootltip = (description, labelFormat = DESCRIPTION.SUPPLIER) => {
  const { city, country, postalCode, street } = description?.locationExternal?.[labelFormat[ADDRESS]] ?? {};

  return [description?.locationExternal?.[labelFormat[NUMBER]], country, postalCode, city, street]
    .filter((data) => removeSpacesFromString(data))
    .join(', ');
};

const LOCATION_INTERNAL = 'locationInternal';
const LOCATION_EXTERNAL = 'locationExternal';
const COST_CENTER = 'costCenter';

type ObjectLike = Record<string, object | boolean | string | number | undefined>;

const toRecord = (value: object | boolean | string | number | null | undefined): ObjectLike => (
  (value && typeof value === 'object') ? value as ObjectLike : {}
);

const TIER1_INPUT_CONFIG = {
  LOCATION: LOCATION_EXTERNAL,
  LABEL: DESCRIPTION.TIER1_SUPPLIER.FIELD,
  NUMBER: {
    ID: POSSIBLE_CAUSER_INPUT_IDS.TIER1_SUPPLIER_NUMBER,
    FIELD: 'tier1SupplierNumber',
  },
  NAME: {
    ID: POSSIBLE_CAUSER_INPUT_IDS.TIER1_SUPPLIER_NAME,
    FIELD: 'tier1SupplierName',
  },
};

const useStyles = makeStyles(() => ({
  oneColumns: { width: '100%', margin: '0 0 20px 0' },
  supplierInput: {
    width: '100%',
    '& .MuiInputBase-input': { textOverflow: 'ellipsis', overflow: 'hidden' },
  },
}));

interface PossibleCauserSectionProps {
  description: DescriptionType;
  headerLanguage: string;
  checkDisabled: boolean;
  updateState: (newState: DescriptionType) => void;
  validateField: (label: string, value: string | undefined) => void;
  descValidations: Validations;
  setDescValidations: (validations: Validations) => void;
  setHelpArea: (help: string) => void;
  campaignInfo: CampaignInfo;
  docmasterLinkParam: LangTextMandatory;
  isNotMP: boolean;
}

type LocationInternalWithConfirm = DescriptionType[typeof LOCATION_INTERNAL] & {
  confirmPlantToPlant?: boolean;
};

type DescriptionSectionName = typeof LOCATION_INTERNAL | typeof LOCATION_EXTERNAL;

type ValidationAttribute = {
  invalid: boolean;
  errors: {
    max?: boolean;
    min?: boolean;
    required?: boolean;
  };
};

const EMPTY_VALIDATION_ATTRIBUTE: ValidationAttribute = { invalid: false, errors: {} };

const PossibleCauserSection: React.FC<PossibleCauserSectionProps> = (props) => {
  const { description, headerLanguage, checkDisabled, updateState, validateField, descValidations,
    setDescValidations, setHelpArea, campaignInfo, docmasterLinkParam, isNotMP } = props;

  const classes = useStyles();
  const { t } = useTranslation();
  const languageValidations = toRecord(descValidations?.[headerLanguage]);
  const getValidationField = (...path: string[]) => {
    const field = path.reduce<object | boolean | string | number | undefined>(
      (current, currentPath) => toRecord((typeof current === 'object' ? current : undefined))[currentPath],
      languageValidations,
    );

    if (field && typeof field === 'object') {
      const fieldRecord = toRecord(field);

      return {
        invalid: Boolean(fieldRecord.invalid),
        errors: toRecord(fieldRecord.errors) as ValidationAttribute['errors'],
      };
    }

    return EMPTY_VALIDATION_ATTRIBUTE;
  };

  const internalExternalWarning = useWarningField([stateTabs.description, 'locationInternalIsChosen'], fieldTypes.CUSTOM_ARRAY);
  const plantsWarning = useWarningField([stateTabs.description, 'plants'], fieldTypes.CUSTOM_ARRAY);
  const costCoverageAcknowledgedInvalid = getValidationField(LOCATION_EXTERNAL, DESCRIPTION.COST_COVERAGE_ACKNOWLEDGED)?.invalid;
  const confirmPlantToPlantInvalid = getValidationField(LOCATION_INTERNAL, DESCRIPTION.POSSIBLE_CAUSER_INTERNAL.CONFIRM_PLANT_TO_PLANT)?.invalid;
  const tier1SupplierAcknowledgedInvalid = getValidationField(LOCATION_EXTERNAL, DESCRIPTION.POSSIBLE_CAUSER_EXTERNAL.TIER1_SUPP_ACKN)?.invalid;

  const handleChangePlant = (label: string, plantName: string, value: string) => {
    const plantsEdited = description.plants.map((currentPlant) => {
      if (currentPlant.plantName === plantName) {
        currentPlant[label] = value;
        validateField(`${plantName}_${label}`, value);
      }

      return currentPlant;
    });
    updateState({ ...description, plants: plantsEdited });
  };

  const resetPlantsFields = (fieldName: string) => {
    const fields = description?.plants?.map(({ plantName }) => {
      handleChangePlant(fieldName, plantName, '');

      return `${plantName}_${fieldName}`;
    }) || [];

    let newState: ObjectLike = { ...languageValidations };
    fields.forEach((currentField) => {
      const currentFieldState = toRecord(newState[currentField]);
      const currentErrors = toRecord(currentFieldState.errors);

      newState = {
        ...newState,
        [currentField]: {
          ...currentFieldState,
          errors: { ...currentErrors, required: false, max: false },
        },
      };
    });

    setDescValidations({ ...descValidations, [headerLanguage]: newState } as Validations);
  };

  const clearCheckboxRequiredValidation = (sectionName, fieldName) => {
    const sectionValidations = toRecord(languageValidations?.[sectionName]);
    const fieldValidations = toRecord(sectionValidations?.[fieldName]);
    const fieldErrors = toRecord(fieldValidations?.errors);

    const updatedLanguageValidations = {
      ...languageValidations,
      [sectionName]: {
        ...sectionValidations,
        [fieldName]: {
          ...fieldValidations,
          invalid: false,
          errors: {
            ...fieldErrors,
            required: false,
          },
        },
      },
    };

    setDescValidations({ ...descValidations, [headerLanguage]: updatedLanguageValidations });
  };

  const toggleCheckboxAndClearValidation = (
    sectionName: DescriptionSectionName,
    fieldName: string,
    currentValue: boolean,
  ) => {
    const updatedSection = {
      ...(description?.[sectionName] || {}),
      [fieldName]: currentValue,
    };

    clearCheckboxRequiredValidation(sectionName, fieldName);
    updateState({ ...description, [sectionName]: updatedSection });
  };

  const createCheckboxWarnHandler = (sectionName: DescriptionSectionName, fieldName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = Boolean(event?.target?.checked);

    toggleCheckboxAndClearValidation(sectionName, fieldName, currentValue);
  };

  const handleChangeLocation = (ev) => {
    const location = (ev.target.value === 'true');
    resetPlantsFields(location ? 'plantAccountingNumber' : 'plantPsNumber');
    updateState({ ...description, locationInternalIsChosen: location }); // @check
  };

  const updateUserPossibleCauser = (userSelected) => {
    const internal = {
      ...description.locationInternal,
      possibleCauser: buildNewSelectedUser(userSelected),
      costCenter: userSelected?.costCenter,
    };

    updateState({ ...description, locationInternal: internal });
    validateField(DESCRIPTION.POSSIBLE_CAUSER, internal.possibleCauser.code);
  };

  const supplierNewState = (supplier, labelFormat) => ({
    [labelFormat[NUMBER]]: supplier ? supplier.id : '',
    [labelFormat[NAME]]: supplier ? supplier.name : '',
    [labelFormat[ADDRESS]]: {
      city: supplier?.addressCity || '',
      country: supplier?.addressCountry || '',
      postalCode: supplier?.addressPostalCode || '',
      street: supplier?.addressStreet || '',
    },
  });

  const handleChangeData = (value, label, location) => {
    const labelFormat = (label === DESCRIPTION.TIER1_SUPPLIER.FIELD) ? DESCRIPTION.TIER1_SUPPLIER : DESCRIPTION.SUPPLIER;
    const data = {
      ...description[location],
      ...((label === DESCRIPTION.SUPPLIER.FIELD || label === DESCRIPTION.TIER1_SUPPLIER.FIELD)
        ? supplierNewState(value, labelFormat)
        : { [label]: value }
      ),
    };

    updateState({ ...description, [location]: data });
  };

  const updateUserContactInfo = (userSelected, field) => {
    const contactPerson = {
      ...description.locationExternal,
      [field]: buildNewSelectedUser(userSelected),
    };
    updateState({ ...description, locationExternal: contactPerson });
    validateField(field, contactPerson[field].code);
  };

  return (
    <AccordionPanel
      title={t('involvedParties.possibleCauser')}
      handleClick={() => setHelpArea(HELP_AREA.POSSIBLE_CAUSER)}
    >
      <div className={`${classes.oneColumns} ${internalExternalWarning ? styles.WarningIntExt : ''}`}>
        <FormControl disabled={checkDisabled} component="fieldset">
          <FormLabel component="legend">
            {t('involvedParties.location')}
          </FormLabel>
          <RadioGroup
            row
            aria-label="position"
            name="position"
            onChange={handleChangeLocation}
            value={description.locationInternalIsChosen}
          >
            <FormControlLabel value control={<Radio color="primary" />} label={t('involvedParties.internal')} />
            <FormControlLabel value={false} control={<Radio color="primary" />} label={t('involvedParties.external')} />
          </RadioGroup>
        </FormControl>
        {!description.locationInternalIsChosen && (
          <DisclaimerCheckbox
            containerStyle={`${styles.MessageStyle} ${costCoverageAcknowledgedInvalid ? 'error' : ''}`}
            id="cost-coverage-acknowledged"
            required
            checked={description.locationExternal.costCoverageAcknowledged}
            onChange={createCheckboxWarnHandler(LOCATION_EXTERNAL, DESCRIPTION.COST_COVERAGE_ACKNOWLEDGED)}
            message="description.externalLocationAwareness"
          />
        )}
      </div>
      <ContainerGrid columns={{ xs: 12, sm: 12, md: 12, lg: 12 }} gap={16}>
        {description.locationInternalIsChosen && (
          <>
            <ContainerGridItem span={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <InputUser
                id="possible-causer"
                disabled={checkDisabled}
                isMandatory
                label={t('involvedParties.possibleCauser')}
                value={description.locationInternal?.possibleCauser.summary}
                updateUserSelected={(userSelected) => updateUserPossibleCauser(userSelected)}
                code={description.locationInternal?.possibleCauser?.code}
                title={t('involvedParties.possibleCauser')}
                clear={() => { updateUserPossibleCauser(null); }}
                fieldPath={[stateTabs.description, LOCATION_INTERNAL, DESCRIPTION.POSSIBLE_CAUSER]}
                userFieldPath={[stateTabs.description, LOCATION_INTERNAL, DESCRIPTION.POSSIBLE_CAUSER]}
                helperText={getValidationField(LOCATION_INTERNAL, DESCRIPTION.POSSIBLE_CAUSER)?.invalid
                  ? [getMessage(getValidationField(LOCATION_INTERNAL, DESCRIPTION.POSSIBLE_CAUSER), null, t)]
                  : []}
                formControlClass="w-100"
              />
            </ContainerGridItem>
            <ContainerGridItem span={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <InputText
                id="cost-center"
                disabled={checkDisabled}
                label={t('involvedParties.costCenter')}
                handleChange={(ev) => handleChangeData(ev.target.value, COST_CENTER, LOCATION_INTERNAL)}
                value={description.locationInternal.costCenter}
                fieldPath={[stateTabs.description, LOCATION_INTERNAL, COST_CENTER]}
                noTranslationField
                formControlClass="w-100"
              />
            </ContainerGridItem>
            {description.locationInternalIsChosen && description.locationInternal?.possibleCauser?.code && (

              <ContainerGridItem span={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <DisclaimerCheckbox
                  containerStyle={`${styles.MessageStyle} ${confirmPlantToPlantInvalid ? 'error' : ''}`}
                  id="confirm-plant-to-plant"
                  required
                  disabled={campaignInfo?.status !== statusCampaign.IN_CREATION}
                  checked={Boolean((description.locationInternal as LocationInternalWithConfirm)?.confirmPlantToPlant)}
                  onChange={createCheckboxWarnHandler(LOCATION_INTERNAL, DESCRIPTION.POSSIBLE_CAUSER_INTERNAL.CONFIRM_PLANT_TO_PLANT)}
                  message="involvedParties.confirmPlanToPlant"
                />

              </ContainerGridItem>
            )}
            {description.plants.map((currentPlant, index) => (
              <ContainerGridItem span={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={currentPlant.wkzNumber}>
                <InputText
                  id={currentPlant.plantName}
                  isPlantInput
                  disabled={checkDisabled}
                  invalid={descValidations?.[headerLanguage]?.[`${currentPlant.plantName}_plantAccountingNumber`]?.invalid
                    || plantsWarning}
                  fieldPath={[stateTabs.description, `plantsDetail[${index}]`]}
                  maxLength={64}
                  label={t('involvedParties.plantAccountingNumber', { x: currentPlant.wkzNumber })}
                  handleChange={(ev) => handleChangePlant('plantAccountingNumber', currentPlant.plantName, ev.target.value)}
                  value={currentPlant.plantAccountingNumber}
                  helperText={[
                    ...(!checkDisabled && descValidations?.[headerLanguage]?.[`${currentPlant.plantName}_plantAccountingNumber`]?.invalid
                      ? [t('validations.maxLength', { maxLength: 64 })]
                      : []),
                    ...(!checkDisabled && currentPlant.plantAccountingNumber?.length === 64
                      ? [t('validations.maxLength', { maxLength: 64 })]
                      : []),
                  ]}
                  formControlClass="w-100"
                />
              </ContainerGridItem>
            ))}
          </>
        )}
        {!description.locationInternalIsChosen && (
          <>
            <ContainerGridItem span={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <InputUser
                id="contact-person-for-supplier-management"
                isMandatory
                disabled={checkDisabled}
                fieldPath={[stateTabs.description, LOCATION_EXTERNAL, DESCRIPTION.CONTACT_PERSON_SUPPL_MANAGEMENT]}
                userFieldPath={[stateTabs.description, LOCATION_EXTERNAL, DESCRIPTION.CONTACT_PERSON_SUPPL_MANAGEMENT]}
                label={t('involvedParties.contactPersonForSupplierManagement')}
                value={description.locationExternal?.contactPersonSupplierManagement?.summary || ''}
                updateUserSelected={(userSelected) => updateUserContactInfo(userSelected, DESCRIPTION.CONTACT_PERSON_SUPPL_MANAGEMENT)}
                code={description.locationExternal?.contactPersonSupplierManagement?.code || ''}
                title={t('involvedParties.contactPersonForSupplierManagement')}
                clear={() => { updateUserContactInfo(null, DESCRIPTION.CONTACT_PERSON_SUPPL_MANAGEMENT); }}
                helperText={getValidationField(LOCATION_EXTERNAL, DESCRIPTION.CONTACT_PERSON_SUPPL_MANAGEMENT)?.invalid
                  ? [getMessage(getValidationField(LOCATION_EXTERNAL, DESCRIPTION.CONTACT_PERSON_SUPPL_MANAGEMENT), null, t)]
                  : []}
                formControlClass="w-100"
              />

            </ContainerGridItem>
            {isNotMP && (
              <ContainerGridItem span={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <InputUser
                  id="additional-contact-person"
                  isMandatory={isNotMP}
                  disabled={checkDisabled}
                  fieldPath={[stateTabs.description, LOCATION_EXTERNAL, additionalContactPerson]}
                  userFieldPath={[stateTabs.description, LOCATION_EXTERNAL, additionalContactPerson]}
                  label={t('involvedParties.additionalContactPerson')}
                  value={description.locationExternal?.additionalContactPerson?.summary || ''}
                  updateUserSelected={(userSelected) => updateUserContactInfo(userSelected, additionalContactPerson)}
                  code={description.locationExternal?.additionalContactPerson?.code || ''}
                  title={t('involvedParties.additionalContactPerson')}
                  clear={() => { updateUserContactInfo(null, additionalContactPerson); }}
                  helperText={getValidationField(LOCATION_EXTERNAL, additionalContactPerson)?.invalid
                    ? [getMessage(getValidationField(LOCATION_EXTERNAL, additionalContactPerson), null, t)]
                    : []}
                  formControlClass="w-100"
                />
              </ContainerGridItem>
            )}
            <ContainerGridItem span={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Calendar
                id="date-picker-description-failureOccurredOn"
                disabled={checkDisabled}
                label={t('involvedParties.costTakeover')}
                fieldPath={[stateTabs.description, LOCATION_EXTERNAL, 'costTakeOverAgreedOn']}
                handleChange={(date) => checkDate({
                  date,
                  label: 'costTakeOverAgreedOn',
                  handleChange: (label, value) => handleChangeData(value, label, 'locationExternal'),
                })}
                value={stringToDate(description.locationExternal?.costTakeOverAgreedOn)}
                margin="none"
                formControlClass="m-all-0 w-100"
              />
            </ContainerGridItem>
            <ContainerGridItem span={{ xs: 12, sm: 12, md: 12, lg: 12 }} />
            <ContainerGridItem span={{ xs: 12, sm: 4, md: 3, lg: 3 }}>
              <InputUser
                id="supplier-number"
                isSupplier
                disabled={checkDisabled || isFieldDisabledBYPRRD(campaignInfo, 'description.locationExternal.supplierNumber')}
                label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="involvedParties.supplierID" fieldName="description.locationExternal.supplierNumber" />}
                updateUserSelected={(supplierSelected) => handleChangeData(supplierSelected, 'supplier', 'locationExternal')}
                fieldPath={[stateTabs.description, LOCATION_EXTERNAL, 'supplier']}
                userFieldPath={[stateTabs.description, LOCATION_EXTERNAL, 'supplier']}
                value={description.locationExternal.supplierNumber}
                title={t('involvedParties.supplier')}
                formControlClass={`${classes.supplierInput} w-100`}
                clear={() => handleChangeData(null, 'supplier', 'locationExternal')}
                tooltipTitle={getSupplierInputTootltip(description)}
                code={description.locationExternal.supplierNumber}
              />
            </ContainerGridItem>
            <ContainerGridItem span={{ xs: 12, sm: 4, md: 3, lg: 3 }}>
              <InputUser
                id="supplier-name"
                isSupplier
                disabled={checkDisabled || isFieldDisabledBYPRRD(campaignInfo, 'description.locationExternal.supplierName')}
                label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="involvedParties.supplierName" fieldName="description.locationExternal.supplierName" />}
                updateUserSelected={(supplierSelected) => handleChangeData(supplierSelected, 'supplier', 'locationExternal')}
                fieldPath={[stateTabs.description, LOCATION_EXTERNAL, 'supplierName']}
                userFieldPath={[stateTabs.description, LOCATION_EXTERNAL, 'supplierName']}
                value={description.locationExternal.supplierName}
                title={t('involvedParties.supplier')}
                formControlClass={`${classes.supplierInput} w-100`}
                clear={() => handleChangeData(null, 'supplier', 'locationExternal')}
                tooltipTitle={getSupplierInputTootltip(description)}
                code={description.locationExternal.supplierNumber}
              />
            </ContainerGridItem>
            <ContainerGridItem span={{ xs: 12, sm: 4, md: 4, lg: 3 }}>
              <div className={`w-100 ${styles.DocMasterLink}`}>
                <a target="_blank" href={docmasterLinkParam?.[headerLanguage]} rel="noreferrer">
                  {t('description.linkToDocMaster', { lng: headerLanguage?.toLowerCase() })}
                </a>
              </div>
            </ContainerGridItem>
            <ContainerGridItem span={{ xs: 12, sm: 12, md: 12, lg: 12 }} />
            <ContainerGridItem span={{ xs: 12, sm: 4, md: 3, lg: 3 }}>
              <InputUser
                id={TIER1_INPUT_CONFIG.NUMBER.ID}
                isSupplier
                disabled={checkDisabled || isFieldDisabledBYPRRD(campaignInfo, 'description.locationExternal.tier1SupplierNumber')}
                label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="involvedParties.tier1SupplierNumber" fieldName="description.locationExternal.tier1SupplierNumber" />}
                updateUserSelected={(supplierSelected) => handleChangeData(supplierSelected, TIER1_INPUT_CONFIG.LABEL, TIER1_INPUT_CONFIG.LOCATION)}
                fieldPath={[stateTabs.description, TIER1_INPUT_CONFIG.LOCATION, TIER1_INPUT_CONFIG.NUMBER.FIELD]}
                userFieldPath={[stateTabs.description, TIER1_INPUT_CONFIG.LOCATION, TIER1_INPUT_CONFIG.NUMBER.FIELD]}
                value={description.locationExternal[TIER1_INPUT_CONFIG.NUMBER.FIELD]}
                title={t('involvedParties.tier1SupplierNumber')}
                formControlClass={`${classes.supplierInput} w-100`}
                clear={() => handleChangeData(null, TIER1_INPUT_CONFIG.LABEL, TIER1_INPUT_CONFIG.LOCATION)}
                tooltipTitle={getSupplierInputTootltip(description, DESCRIPTION.TIER1_SUPPLIER)}
                code={description.locationExternal[TIER1_INPUT_CONFIG.NUMBER.FIELD]}
              />
            </ContainerGridItem>
            <ContainerGridItem span={{ xs: 12, sm: 4, md: 3, lg: 3 }}>
              <InputUser
                id={TIER1_INPUT_CONFIG.NAME.ID}
                isSupplier
                disabled={checkDisabled || isFieldDisabledBYPRRD(campaignInfo, 'description.locationExternal.tier1SupplierName')}
                label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="involvedParties.tier1SupplierName" fieldName="description.locationExternal.tier1SupplierName" />}
                updateUserSelected={(supplierSelected) => handleChangeData(supplierSelected, TIER1_INPUT_CONFIG.LABEL, TIER1_INPUT_CONFIG.LOCATION)}
                fieldPath={[stateTabs.description, TIER1_INPUT_CONFIG.LOCATION, TIER1_INPUT_CONFIG.NAME.FIELD]}
                userFieldPath={[stateTabs.description, TIER1_INPUT_CONFIG.LOCATION, TIER1_INPUT_CONFIG.NAME.FIELD]}
                value={description.locationExternal[TIER1_INPUT_CONFIG.NAME.FIELD]}
                title={t('involvedParties.tier1SupplierName')}
                formControlClass={`${classes.supplierInput} w-100`}
                clear={() => handleChangeData(null, TIER1_INPUT_CONFIG.LABEL, TIER1_INPUT_CONFIG.LOCATION)}
                tooltipTitle={getSupplierInputTootltip(description, DESCRIPTION.TIER1_SUPPLIER)}
                code={description.locationExternal[TIER1_INPUT_CONFIG.NUMBER.FIELD]}
              />
            </ContainerGridItem>

            <ContainerGridItem span={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <DisclaimerCheckbox
                containerStyle={`${styles.MessageStyle} ${tier1SupplierAcknowledgedInvalid ? 'error' : ''}`}
                id="tier1-supplier-acknowledged"
                required={!!(description.locationExternal?.supplierName || description.locationExternal?.supplierNumber)}
                checked={!!description.locationExternal.tier1SupplierAcknowledged}
                onChange={createCheckboxWarnHandler(LOCATION_EXTERNAL, DESCRIPTION.POSSIBLE_CAUSER_EXTERNAL.TIER1_SUPP_ACKN)}
                disclaimerHeaderText="description.tier1SupplierAcknowledgement.title"
                message="description.tier1SupplierAcknowledgement.message"
              />
            </ContainerGridItem>

            <ContainerGridItem span={{ xs: 12, sm: 12, md: 12, lg: 12 }} />
            {description.plants.map((currentPlant) => (
              <ContainerGridItem key={currentPlant.wkzNumber} span={{ xs: 12, sm: 8, md: 6, lg: 6 }}>
                <InputText
                  id={currentPlant.plantName}
                  disabled={checkDisabled}
                  invalid={descValidations?.[headerLanguage]?.[`${currentPlant.plantName}_plantPsNumber`]?.invalid || plantsWarning}
                  maxLength={64}
                  tooltipTitle={t('involvedParties.psNumber', { x: currentPlant.plantName })}
                  fieldPath={[stateTabs.description, LOCATION_EXTERNAL, `${currentPlant.plantName}_plantPsNumber`]}
                  label={t('involvedParties.psNumber', { x: currentPlant.plantName })}
                  handleChange={(ev) => handleChangePlant('plantPsNumber', currentPlant.plantName, ev.target.value)}
                  value={currentPlant.plantPsNumber}
                  formControlClass="w-100"
                  helperText={[
                    ...(!checkDisabled && (descValidations?.[headerLanguage]?.[`${currentPlant.plantName}_plantPsNumber`]?.invalid || currentPlant.plantPsNumber?.length === 64)
                      ? [t('validations.maxLength', { maxLength: 64 })]
                      : []),
                  ]}
                />
              </ContainerGridItem>
            ))}
          </>
        )}
      </ContainerGrid>
    </AccordionPanel>
  );
};

const mapStateToProps = (state) => ({
  description: getCampaignDescription(state),
  originalValues: getOriginalValues(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  descValidations: getDescriptionValidations(state),
  campaignInfo: getCampaignInfo(state),
  docmasterLinkParam: getParamConfiguration(state, FIELD_PARAMS.DOCMASTER_LINK),
});

const actionCreators = {
  setDescValidations: setDescriptionValidations,
  setHelpArea: setAreaHelp,
};

export default connect(mapStateToProps, actionCreators)(PossibleCauserSection);
